import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { SiteI18nProvider } from "./site/SiteI18n";
import { SiteLayout } from "./site/SiteLayout";

// Lazy-loaded site routes — each page is its own chunk, keeping the
// homepage bundle minimal and improving LCP across all routes.
const SiteHome = lazy(() => import("./site/pages/SiteHome"));
const SitePlatforma = lazy(() => import("./site/pages/SitePlatforma"));
const SiteSolutii = lazy(() => import("./site/pages/SiteSolutii"));
const SiteSolutiiDetail = lazy(() => import("./site/pages/SiteSolutiiDetail"));
const SiteIndustrii = lazy(() => import("./site/pages/SiteIndustrii"));
const SiteIndustriiDetail = lazy(() => import("./site/pages/SiteIndustriiDetail"));
const SiteDespre = lazy(() => import("./site/pages/SiteDespre"));
const SiteContact = lazy(() => import("./site/pages/SiteContact"));
const SiteResurse = lazy(() => import("./site/pages/SiteResurse"));
const SiteResursaDetail = lazy(() => import("./site/pages/SiteResursaDetail"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-[60vh] grid place-items-center">
    <div className="h-10 w-10 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
  </div>
);

const SiteRoutes = () => (
  <SiteLayout>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Canonical top-level routes */}
        <Route path="/" element={<SiteHome />} />
        <Route path="/platforma" element={<SitePlatforma />} />
        <Route path="/solutii" element={<SiteSolutii />} />
        <Route path="/solutii/:slug" element={<SiteSolutiiDetail />} />
        <Route path="/industrii" element={<SiteIndustrii />} />
        <Route path="/industrii/:slug" element={<SiteIndustriiDetail />} />
        <Route path="/despre" element={<SiteDespre />} />
        <Route path="/contact" element={<SiteContact />} />
        <Route path="/resurse" element={<SiteResurse />} />
        <Route path="/resurse/:slug" element={<SiteResursaDetail />} />

        {/* Legacy /site/* — permanent redirect to canonical URL */}
        <Route path="/site" element={<Navigate to="/" replace />} />
        <Route path="/site/platforma" element={<Navigate to="/platforma" replace />} />
        <Route path="/site/solutii" element={<Navigate to="/solutii" replace />} />
        <Route path="/site/industrii" element={<Navigate to="/industrii" replace />} />
        <Route path="/site/despre" element={<Navigate to="/despre" replace />} />
        <Route path="/site/contact" element={<Navigate to="/contact" replace />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  </SiteLayout>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <SiteI18nProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SiteRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </SiteI18nProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
